import { cn } from '@/lib/utils';
import { Plus, MessageSquare, Trash2, FileText, CheckCircle2, Clock, ShieldCheck, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLayoutStore } from '@/stores/useLayoutStore';
import type { InterviewSession, SessionReport, InterviewViewMode } from './useInterviewStore';
import { useCallback, useRef } from 'react';

interface SessionSidebarProps {
  sessions: InterviewSession[];
  activeSessionId: string | null;
  activeReportId: string | null;
  viewMode: InterviewViewMode;
  sessionReports: Record<string, SessionReport[]>;
  loading: boolean;
  isRegenerating?: boolean;
  fullWidth?: boolean;
  onSelectSession: (id: string) => void;
  onSelectReport: (sessionId: string, reportId: string) => void;
  onNewSession: () => void;
  onDeleteSession: (id: string) => void;
  onRegenerateReport?: (reportId: string, sessionId: string) => void;
}

const reportStatusLabels: Record<string, string> = {
  PENDING_REVIEW: 'Zu prüfen',
  FINALIZED: 'Finalisiert',
  OUTDATED: 'Veraltet',
};

const reportStatusColors: Record<string, string> = {
  PENDING_REVIEW: 'text-blue-600 dark:text-blue-400',
  FINALIZED: 'text-emerald-600 dark:text-emerald-400',
  OUTDATED: 'text-zinc-500 dark:text-zinc-400',
};

export function SessionSidebar({
  sessions,
  activeSessionId,
  activeReportId,
  viewMode,
  sessionReports,
  loading,
  isRegenerating,
  fullWidth = false,
  onSelectSession,
  onSelectReport,
  onNewSession,
  onDeleteSession,
  onRegenerateReport,
}: SessionSidebarProps) {
  const interviewSidebarWidth = useLayoutStore((s) => s.interviewSidebarWidth);
  const setInterviewSidebarWidth = useLayoutStore((s) => s.setInterviewSidebarWidth);
  const isResizing = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      // The interview sidebar starts after the main app sidebar,
      // so we need to calculate offset from its left edge.
      const sidebarEl = (e.target as HTMLElement).previousElementSibling;
      if (sidebarEl) {
        const rect = sidebarEl.getBoundingClientRect();
        setInterviewSidebarWidth(ev.clientX - rect.left);
      }
    };
    const onMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [setInterviewSidebarWidth]);

  return (
    <div className={cn('flex h-full', fullWidth ? 'w-full' : 'shrink-0')}>
      <div
        className="flex h-full flex-col rounded-lg border border-border bg-card/60 backdrop-blur-[14px] overflow-hidden"
        style={fullWidth ? { width: '100%' } : { width: `${interviewSidebarWidth}px` }}
      >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">Interviews</h3>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onNewSession}
          title="Neues Interview"
          aria-label="Neues Interview starten"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {loading && (
          <div className="space-y-2 p-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-12 animate-pulse rounded-lg bg-muted"
              />
            ))}
          </div>
        )}
        {!loading && sessions.length === 0 && (
          <p className="p-4 text-center text-xs text-muted-foreground">
            Noch keine Interviews.
            <br />
            Starte dein erstes Interview!
          </p>
        )}
        {sessions.map((session) => {
          const reports = sessionReports[session.id] ?? [];
          const isActiveSession = session.id === activeSessionId;
          const isChatActive = isActiveSession && viewMode === 'chat';

          return (
            <div key={session.id}>
              {/* Session row */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => onSelectSession(session.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectSession(session.id);
                  }
                }}
                className={cn(
                  'group flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left transition-colors',
                  isChatActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-medium">
                      {session.topic || 'Interview'}
                      <span className="ml-1 text-[10px] font-normal opacity-50">
                        {new Date(session.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </p>
                    {session.status === 'Completed' && (
                      <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-400" />
                    )}
                  </div>
                  <p className="text-[10px] opacity-60">
                    {session.messageCount} Nachrichten
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSession(session.id);
                  }}
                  className="mt-0.5 hidden h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/50
                             hover:bg-destructive/10 hover:text-destructive group-hover:flex"
                  title="Interview löschen"
                  aria-label="Interview löschen"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>

              {/* Report children (shown for completed sessions) */}
              {reports.map((report) => {
                const isReportActive = isActiveSession && viewMode === 'report' && activeReportId === report.id;
                const statusIcon = report.status === 'FINALIZED'
                  ? <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                  : report.status === 'PENDING_REVIEW'
                    ? <ShieldCheck className="h-3 w-3 text-blue-400" />
                    : <Clock className="h-3 w-3 text-zinc-400" />;

                return (
                  <button
                    key={report.id}
                    onClick={() => onSelectReport(session.id, report.id)}
                    className={cn(
                      'group flex w-full items-center gap-2 rounded-md px-3 py-1.5 pl-9 text-left transition-colors',
                      isReportActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">Report</p>
                      <p className={cn('text-[10px]', reportStatusColors[report.status] ?? 'opacity-60')}>
                        {reportStatusLabels[report.status] ?? report.status} · {report.entityCount} Entitäten
                      </p>
                    </div>
                    {onRegenerateReport && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRegenerateReport(report.id, session.id);
                        }}
                        className="hidden h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/50
                                   hover:bg-primary/10 hover:text-primary group-hover:flex"
                        title="Report neu generieren"
                        aria-label="Report neu generieren"
                      >
                        {isRegenerating
                          ? <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" />
                          : <RefreshCw className="h-3 w-3" />}
                      </button>
                    )}
                    {statusIcon}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      </div>
      {/* Resize handle – hidden in full-width (mobile) mode */}
      {!fullWidth && (
        <div
          onMouseDown={handleMouseDown}
          className="w-1 shrink-0 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors"
        />
      )}
    </div>
  );
}
